import OptimizedImage from "@/components/OptimizedImage";
import React, { useState } from "react";
import { Sparkles, Star, Building2, GraduationCap, X } from "lucide-react";
import { usePerformanceDetailsForPreview } from "@/api/hooks/usePerformanceDetailsForPreview";
import { usePastPerformancesForPreview } from "@/api/hooks/usePastPerformancesForPreview";
import { usePastPerformancesStore } from "@/stores/pastPerformancesStore";
import { Loader } from "@/components/ui/Loader";

const ELITE_ICONS = [
  <Sparkles key="s" className="w-6 h-6 mr-2 text-rose-400" />,
  <Building2 key="b" className="w-6 h-6 mr-2 text-blue-600" />,
  <GraduationCap key="g" className="w-6 h-6 mr-2 text-green-600" />,
  <Star key="t" className="w-6 h-6 mr-2 text-indigo-600" />,
];

interface ArtistElitePerformanceProps {
  hideHero?: boolean;
  hidePastHeading?: boolean;
  hideImage?: boolean;
  enableDelete?: boolean;
}

const ArtistElitePerformance: React.FC<ArtistElitePerformanceProps> = ({
  hideHero = false,
  hidePastHeading = false,
  hideImage = false,
  enableDelete = false,
}) => {
  const {
    performingCount,
    offstageCount,
    durationText,
    openToTravel,
    languages,
  } = usePerformanceDetailsForPreview();
  const { performances } = usePastPerformancesForPreview();
  const deleteItem = usePastPerformancesStore((s) => s.deleteItem);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const pastPerformances = performances.map((p, i) => ({
    id: p.id,
    type: p.event_name,
    count:
      p.event_count > 0
        ? `${p.event_count}${p.event_count >= 10 ? "+" : ""} Events`
        : "0 Events",
    popular: p.desc || "—",
    icon: ELITE_ICONS[i % ELITE_ICONS.length],
  }));

  return (
    <div className="md:px-5 pb-20 w-screen">
      {!hideHero && (
        <div className="px-2">
          <div className="z-10 relative overflow-hidden mb-40 bg-[#10121A] rounded-[6rem] max-sm:rounded-xl px-12 max-lg:px-4 lg:py-18 xl:py-24">
            <svg
              className="absolute top-0 left-0"
              width="522"
              height="450"
              viewBox="0 0 522 450"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g filter="url(#filter0_b_28_5333)">
                <path
                  d="M265.242 -51.437C402.416 -53.6648 515.423 55.7305 517.651 192.904C519.879 330.078 410.483 443.085 273.31 445.313L256.484 445.586C119.287 447.814 6.2613 338.401 4.03319 201.204C1.80581 64.0534 111.183 -48.935 248.334 -51.1624L265.242 -51.437Z"
                  fill="url(#paint0_linear_28_5333)"
                  fillOpacity="0.55"
                />
              </g>
              <defs>
                <filter
                  id="filter0_b_28_5333"
                  x="-336.521"
                  y="-391.992"
                  width="1194.73"
                  height="1178.13"
                  filterUnits="userSpaceOnUse"
                  colorInterpolationFilters="sRGB"
                >
                  <feFlood floodOpacity="0" result="BackgroundImageFix" />
                  <feGaussianBlur
                    in="BackgroundImageFix"
                    stdDeviation="168.261"
                  />
                  <feComposite
                    in2="SourceAlpha"
                    operator="in"
                    result="effect1_backgroundBlur_28_5333"
                  />
                  <feBlend
                    mode="normal"
                    in="SourceGraphic"
                    in2="effect1_backgroundBlur_28_5333"
                    result="shape"
                  />
                </filter>
                <linearGradient
                  id="paint0_linear_28_5333"
                  x1="533.5"
                  y1="396.529"
                  x2="211.675"
                  y2="104.372"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="#B0562F" stopOpacity="0.63" />
                  <stop offset="1" stopColor="#090A07" stopOpacity="0.02" />
                </linearGradient>
              </defs>
            </svg>
            <svg
              className="absolute bottom-0 right-0"
              width="239"
              height="387"
              viewBox="0 0 239 387"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g filter="url(#filter0_b_28_5328)">
                <path
                  d="M158.495 1.83227C217.882 0.867798 266.807 48.229 267.772 107.616L267.951 118.649C268.915 178.026 221.562 226.943 162.184 227.908C102.807 228.872 53.8897 181.519 52.9253 122.141L52.7456 111.073C51.7814 51.7052 99.127 2.79642 158.495 1.83227Z"
                  fill="url(#paint0_linear_28_5328)"
                />
              </g>
              <g filter="url(#filter1_b_28_5328)">
                <path
                  d="M1.33702 406.467C2.73224 231.98 145.314 91.6603 319.801 93.0556L315.938 576.19L0.000105165 573.664L1.33702 406.467Z"
                  fill="url(#paint1_linear_28_5328)"
                  fillOpacity="0.41"
                />
              </g>
              <defs>
                <filter
                  id="filter0_b_28_5328"
                  x="-43.3062"
                  y="-94.2203"
                  width="407.31"
                  height="418.18"
                  filterUnits="userSpaceOnUse"
                  colorInterpolationFilters="sRGB"
                >
                  <feFlood floodOpacity="0" result="BackgroundImageFix" />
                  <feGaussianBlur
                    in="BackgroundImageFix"
                    stdDeviation="47.1531"
                  />
                  <feComposite
                    in2="SourceAlpha"
                    operator="in"
                    result="effect1_backgroundBlur_28_5328"
                  />
                  <feBlend
                    mode="normal"
                    in="SourceGraphic"
                    in2="effect1_backgroundBlur_28_5328"
                    result="shape"
                  />
                </filter>
                <filter
                  id="filter1_b_28_5328"
                  x="-71.741"
                  y="18.7883"
                  width="463.284"
                  height="629.143"
                  filterUnits="userSpaceOnUse"
                  colorInterpolationFilters="sRGB"
                >
                  <feFlood floodOpacity="0" result="BackgroundImageFix" />
                  <feGaussianBlur
                    in="BackgroundImageFix"
                    stdDeviation="35.8705"
                  />
                  <feComposite
                    in2="SourceAlpha"
                    operator="in"
                    result="effect1_backgroundBlur_28_5328"
                  />
                  <feBlend
                    mode="normal"
                    in="SourceGraphic"
                    in2="effect1_backgroundBlur_28_5328"
                    result="shape"
                  />
                </filter>
                <linearGradient
                  id="paint0_linear_28_5328"
                  x1="252.584"
                  y1="187.17"
                  x2="49.4804"
                  y2="23.1689"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="#771A58" />
                  <stop offset="1" stopOpacity="0.02" />
                </linearGradient>
                <linearGradient
                  id="paint1_linear_28_5328"
                  x1="264.641"
                  y1="129.635"
                  x2="-103.548"
                  y2="399.45"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="#7F7646" />
                  <stop offset="1" stopOpacity="0.02" />
                </linearGradient>
              </defs>
            </svg>

            <div className="flex w-full flex-col justify-between max-lg:flex-col rounded-[80px] max-lg:rounded-xl py-6 gap-10 relative z-10 overflow-hidden ">
              <p className="text-[58px] font-[600] max-lg:text-5xl max-sm:text-4xl leading-[67.77px] max-md:leading-none  bg-gradient-to-r from-[#F16B2E] via-[#EC2179] to-[#EC2179] text-transparent bg-clip-text px-4">
                Performance
                <span className=" hidden lg:visible">
                  <br />
                </span>{" "}
                Details
              </p>
              <div className="flex flex-col gap-4 ">
                <div className="grid grid-cols-3 max-sm:grid-cols-2 gap-4 ">
                  <div className="rounded-2xl bg-black">
                    <div className="w-full h-full rounded-2xl text-center bg-gray-900/80 p-4 py-10 flex flex-col justify-center items-center">
                      <p className=" text-2xl lg:text-[30px] xl:text-[48px] font-bold leading-[58.09px] text-white">
                        {performingCount}
                      </p>
                      <span className="mt-2 text-[16px] max-sm:text-sm font-[300] leading-[19.36px] text-white">
                        Performing Members
                      </span>
                    </div>
                  </div>
                  <div className="rounded-2xl bg-black">
                    <div className="w-full h-full rounded-2xl text-center bg-gray-900/80 p-4 py-10  flex flex-col justify-center items-center ">
                      <p className="text-2xl lg:text-[30px] xl:text-[48px] font-bold leading-[58.09px] text-white">
                        {offstageCount}
                      </p>
                      <span className="mt-4 text-[16px] max-sm:text-sm font-[300] leading-[19.36px] text-white">
                        Off Stage Members
                      </span>
                    </div>
                  </div>
                  <div className="rounded-2xl bg-black">
                    <div className="w-full h-full rounded-2xl text-center bg-gray-900/80 p-4 py-10  flex flex-col justify-center items-center ">

                      <div className="flex flex-col sm:flex-row justify-center sm:justify-end items-center sm:items-end gap-1">
                        <p className="text-xl lg:text-[20px] xl:text-[28px] leading-none text-white font-bold">
                          {durationText}
                        </p>
                        <p className="-mb-">mins</p>
                      </div>
                      <span className="mt-4 text-[16px] max-sm:text-sm font-[300] leading-[19.36px] text-white">
                        Performance Duration
                      </span>
                    </div>
                  </div>
                  <div className="rounded-2xl bg-black">
                    <div className="w-full h-full rounded-2xl gap-4 text-center bg-gray-900/80 p-4 py-10  flex flex-col justify-center items-center ">
                      <p className="text-xl lg:text-[20px] xl:text-[28px] font-normal leading-none text-white capitalize">
                        {openToTravel}
                      </p>
                      <span className="text-[16px] max-sm:text-sm font-[300] leading-[19.36px] text-white">
                        open to travel
                      </span>
                    </div>
                  </div>
                  <div className="rounded-2xl col-span-2 bg-black">
                    <div className="w-full h-full rounded-2xl text-center bg-gray-900/80 flex flex-col justify-center items-center p-4">
                      <div className="flex flex-wrap justify-center items-center gap-2">
                        {languages.map((lang) => (
                          <p
                            key={lang.l_slug}
                            className="text-sm lg:text-[16px] xl:text-xl font-normal text-black cursor-pointer truncate max-w-[12rem] bg-white/80 rounded-lg p-1 px-2 text-center"
                            title={lang.language_name}
                          >
                            {lang.code}
                          </p>
                        ))}
                      </div>
                      <span className="text-[16px] max-sm:text-sm font-[300] leading-[19.36px] text-white mt-2">
                        Performing Language
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {!hidePastHeading && (
        <h3 className="text-[28px] ml-10 md:text-[42px] font-bold bg-gradient-to-r from-[#F16B2E] via-[#EC2179] to-[#EC2179] text-transparent bg-clip-text mt-16">
          PAST PERFORMANCES
        </h3>
      )}
      <div className="relative mt-5 mr-10">
        {/* Performance Cards Container */}
        <div className="w-full max-w-7xl  mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">

            {/* Cards Section */}
            <div className="w-full lg:pr-8">
              <div className="space-y-4 sm:space-y-5">
                {pastPerformances.map((item, index) => {
                  const isReal = item.id > 0;
                  const canDelete = enableDelete && isReal;
                  const isDeleting = deletingId === item.id;

                  const handleDelete = async () => {
                    if (!canDelete || deletingId !== null) return;
                    try {
                      setDeletingId(item.id as number);
                      await deleteItem(item.id as number);
                    } finally {
                      setDeletingId(null);
                    }
                  };

                  return (
                    <div
                      key={item.id ?? index}
                      className="group border bg-gray-900/80 shadow-[0_0_12px_rgba(255,255,255,0.4)] rounded-xl p-4 sm:p-6 backdrop-blur-sm hover:bg-gray-600/90 transition-all duration-300 hover:shadow-xl hover:scale-[1.02] relative"
                    >
                      {canDelete && (
                        <button
                          type="button"
                          onClick={handleDelete}
                          disabled={isDeleting}
                          className="absolute top-2 right-2 z-50 flex h-6 w-6 items-center justify-center rounded-full bg-black/80 text-xs text-white border border-white/40 disabled:opacity-60"
                        >
                          {isDeleting ? <Loader size="small" /> : <X className="h-3 w-3" />}
                        </button>
                      )}
                    {/* Mobile Layout - Stacked */}
                    <div className="flex flex-col space-y-3 sm:hidden">
                      <div className="flex items-center justify-center">
                        <div className="flex items-center  font-semibold text-lg text-white">
                          <span className="text-2xl text-red-400 ">{item.icon}</span>
                          <span className="text-xl  lg:text-[20px] xl:text-[28px] font-normal leading-none text-white">{item.type}</span>
                        </div>
                      </div>

                      <div className="flex items-center justify-center">
                        <div className="flex items-center font-medium text-gray-200 text-base">
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3" />
                            <circle cx="12" cy="12" r="10" />
                          </svg>
                          {item.count}
                        </div>
                      </div>

                      <div className="flex items-center justify-center">
                        <div className="flex items-center text-gray-300 text-sm text-center">
                          <Star className="w-6 h-6 mr-2 text-indigo-600" />
                          <span className="break-words leading-relaxed">{item.popular}</span>
                        </div>
                      </div>
                    </div>

                    {/* Desktop/Tablet Layout - Horizontal */}
                    <div className="hidden sm:flex sm:items-center sm:justify-between sm:gap-6">
                      <div className="flex items-center font-semibold text-base lg:text-lg text-white min-w-0 flex-1">
                        <span className="text-xl lg:text-2xl mr-3 flex-shrink-0">{item.icon}</span>
                        <span className="truncate">{item.type}</span>
                      </div>

                      <div className="flex items-center font-medium text-gray-200 text-sm lg:text-base flex-shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 lg:w-5 lg:h-5 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3" />
                          <circle cx="12" cy="12" r="10" />
                        </svg>
                        {item.count}
                      </div>

                      <div className="flex items-center text-gray-300 text-sm lg:text-base min-w-0 flex-1 justify-end">
                        <Star className="w-6 h-6 mr-2 text-indigo-600" />
                        <span className="break-words mt-4 text-[16px] max-sm:text-sm font-[300] leading-[19.36px] text-white">{item.popular}</span>
                      </div>
                    </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Image Section */}
            {!hideImage && (
              <div className="w-full lg:w-auto">
                <div className="relative w-full h-64 sm:h-80 lg:h-96 xl:h-[500px]">
                  <OptimizedImage
                    src="https://media.gq.com/photos/68260e4eb21682b92ac7ce6a/master/w_2560%2Cc_limit/2215011809"
                    // src={'/assets/big.png'}
                    width={800}
                    height={600}
                    className="w-full h-full object-cover rounded-xl shadow-lg lg:shadow-2xl"
                    alt="Performance Image"
                  />
                  {/* Optional overlay for better text contrast if needed */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-xl lg:hidden"></div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArtistElitePerformance;
